import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { useDashboardPermissions } from '@/hooks/useDashboardPermissions';

const DEBUG_PERMISSION_ALLOWED_EMAILS = ['pico.dellamirandola@polo9.org'];

type DebugInfo = {
  email: string;
  role: string;
  groups: string[];
  scopes: string[];
  dashboardPermissions: Record<string, unknown> | null;
  businessHubPermissions: Record<string, unknown> | null;
  canAccessDashboardApp: boolean;
  canViewAllScopes: boolean;
  allowedScopes: string[];
  canFilterCdc: boolean;
  route: string;
};

const DebugPanel = () => {
  const location = useLocation();
  const { user, isAdmin, canAccessApp } = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const dashboardPerms = useDashboardPermissions();

  const isEnabled = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('debugPermissions') === '1';
  }, [location.search]);

  const debugInfo = useMemo<DebugInfo | null>(() => {
    if (!user) return null;

    const businessHub = user.permissions?.hubs?.find((hub) => hub.hubId === 'business') || null;
    const dashboardPermission = businessHub?.apps?.dashboard as unknown as Record<string, unknown> | undefined;

    return {
      email: user.email,
      role: user.role,
      groups: user.groups || [],
      scopes: user.scopes || [],
      dashboardPermissions: dashboardPermission || null,
      businessHubPermissions: businessHub as unknown as Record<string, unknown> | null,
      canAccessDashboardApp: canAccessApp('business', 'dashboard'),
      canViewAllScopes: dashboardPerms.fullAccess,
      allowedScopes: dashboardPerms.scopes,
      canFilterCdc: dashboardPerms.canFilterCdc,
      route: `${location.pathname}${location.search}`
    };
  }, [user, canAccessApp, dashboardPerms, location.pathname, location.search]);

  const isAllowedUser = !!user?.email && DEBUG_PERMISSION_ALLOWED_EMAILS.includes(user.email);

  if (!isEnabled || (!isAdmin && !isAllowedUser) || !debugInfo) return null;

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 z-9999 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white shadow-lg hover:bg-red-500"
      >
        Debug Permessi
      </button>
    );
  }

  return (
    <div className="fixed right-4 top-4 z-9999 max-h-[80vh] w-md overflow-auto rounded-xl border border-zinc-700 bg-zinc-950/95 p-4 font-mono text-xs text-white shadow-2xl backdrop-blur">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="m-0 text-sm font-semibold text-amber-300">Debug Permessi</h4>
        <button
          onClick={() => setIsVisible(false)}
          className="rounded bg-red-600 px-2 py-1 text-white hover:bg-red-500"
        >
          ✕
        </button>
      </div>

      <div className="space-y-3 leading-5">
        <div><strong>Email:</strong> {debugInfo.email}</div>
        <div><strong>Role:</strong> {debugInfo.role}</div>
        <div><strong>Route:</strong> {debugInfo.route}</div>
        <div><strong>Can access `business/dashboard`:</strong> {debugInfo.canAccessDashboardApp ? 'true' : 'false'}</div>
        <div><strong>JWT/User scopes:</strong> {JSON.stringify(debugInfo.scopes)}</div>
        <div><strong>Groups used:</strong> {JSON.stringify(debugInfo.groups)}</div>
        <div><strong>Dashboard full access:</strong> {debugInfo.canViewAllScopes ? 'true' : 'false'}</div>
        <div><strong>Dashboard allowed scopes:</strong> {JSON.stringify(debugInfo.allowedScopes)}</div>
        <div><strong>Can filter CDC:</strong> {debugInfo.canFilterCdc ? 'true' : 'false'}</div>

        <div className="border-t border-zinc-700 pt-3">
          <strong>Business hub permissions</strong>
          <pre className="mt-2 overflow-auto rounded bg-zinc-900 p-2 text-[11px] text-zinc-200">{JSON.stringify(debugInfo.businessHubPermissions, null, 2)}</pre>
        </div>

        <div className="border-t border-zinc-700 pt-3">
          <strong>Raw dashboard permissions</strong>
          <pre className="mt-2 overflow-auto rounded bg-zinc-900 p-2 text-[11px] text-zinc-200">{JSON.stringify(debugInfo.dashboardPermissions, null, 2)}</pre>
        </div>
      </div>
    </div>
  );
};

export default DebugPanel;
